import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { HubClient } from '../utils/HubClient';
import {
  getGetBookingDetailDtoQueryKey,
  getGetSettledBookingsV2QueryKey,
  getGetUnsettledBookingsV2QueryKey,
} from '@ordr-as/ordr-service-types/epos';
import { getGetMenuForLocationQueryKey } from '@ordr-as/menu-builder-types/epos';
import { getGetPaymentStateQueryKey } from '@ordr-as/payment-service-types/epos';
import { useLocation } from '../context/LocationContext';
import { useTerminalStore } from '../context/TerminalStore';

export function useHubCacheInvalidation(hub: HubClient) {
  const queryClient = useQueryClient();
  const { location } = useLocation();

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const unsubUpdated = hub.onBroadcast('digitalBill.updated', (payload) => {
      // TODO: Get rid of this delay once we've removed the "split order lines onto new temporary table" for partial payments hack
      //  If we refetch right away, we see the new digital bill floating around, which is confusing
      const timeoutId = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: getGetBookingDetailDtoQueryKey(payload.locationId, payload.reference),
        });
        queryClient.invalidateQueries({ queryKey: getGetUnsettledBookingsV2QueryKey(payload.locationId) });
        queryClient.invalidateQueries({ queryKey: getGetSettledBookingsV2QueryKey(payload.locationId) });
      }, 1000);
      timeouts.push(timeoutId);
    });

    const unsubFullyPaid = hub.onBroadcast('digitalBill.fullyPaid', (payload) => {
      const timeoutId = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: getGetBookingDetailDtoQueryKey(payload.locationId, payload.reference),
        });
        queryClient.invalidateQueries({ queryKey: getGetUnsettledBookingsV2QueryKey(payload.locationId) });
        queryClient.invalidateQueries({ queryKey: getGetSettledBookingsV2QueryKey(payload.locationId) });
      }, 1000);
      timeouts.push(timeoutId);
    });

    const unsubDeleted = hub.onBroadcast('digitalBill.deleted', (payload) => {
      queryClient.invalidateQueries({
        queryKey: getGetBookingDetailDtoQueryKey(payload.locationId, payload.reference),
      });
      queryClient.invalidateQueries({ queryKey: getGetUnsettledBookingsV2QueryKey(payload.locationId) });
    });

    const unsubCreated = hub.onBroadcast('digitalBill.created', (payload) => {
      queryClient.invalidateQueries({ queryKey: getGetUnsettledBookingsV2QueryKey(payload.locationId) });
    });

    const unsubActivePaymentAdded = hub.onBroadcast('payment.started', (payload) => {
      const timeoutId = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['active-payments', payload.locationId] });
      }, 1500);
      timeouts.push(timeoutId);
    });

    const unsubActivePaymentRemoved = hub.onBroadcast('payment.finished', (payload) => {
      const timeoutId = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['active-payments', payload.locationId] });
      }, 1500);
      timeouts.push(timeoutId);
    });

    const unsubActivePaymentRemovedV2 = hub.onBroadcast('payment.finished.v2', (payload) => {
      if (payload.paymentReference) {
        if (location) {
          queryClient.invalidateQueries({
            queryKey: getGetPaymentStateQueryKey(location.id, payload.paymentReference),
          });
        }
        useTerminalStore.getState().actions.clearPayment(payload.paymentReference);
      }
    });

    const invalidateMenuSections = (locationId: number) => {
      queryClient.invalidateQueries({ queryKey: getGetMenuForLocationQueryKey(locationId) });
    };

    // New grouped menu event - handles all menu changes in a single broadcast
    const unsubMenuBatchUpdated = hub.onBroadcast('menu.batchedUpdate', (payload) => {
      invalidateMenuSections(payload.locationId);
    });

    return () => {
      timeouts.forEach((id) => clearTimeout(id));
      unsubUpdated();
      unsubFullyPaid();
      unsubDeleted();
      unsubCreated();
      unsubActivePaymentAdded();
      unsubActivePaymentRemoved();
      unsubActivePaymentRemovedV2();
      unsubMenuBatchUpdated();
    };
  }, [hub, queryClient, location]);
}
